<template>
  <div id="app" :class="{ 'sidebar-open': sidebarOpen }">
    <template v-if="!$route.meta.hideHeader">
      <Sidebar :open="sidebarOpen" @close="sidebarOpen = false" />
      <div class="main-wrapper" @click="closeSidebarOnMobile">
        <Topbar @toggle-sidebar="sidebarOpen = !sidebarOpen" />
        <main class="page-content">
          <router-view />
        </main>
      </div>
    </template>
    <template v-else>
      <router-view />
    </template>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import Topbar from '@/components/Topbar.vue'

export default {
  name: 'App',
  components: { Sidebar, Topbar },
  data() {
    return { sidebarOpen: window.innerWidth >= 992 }
  },
  watch: {
    '$route'(to) {
      if (to.meta.hideHeader) {
        document.body.classList.remove('wb-layout');
      } else {
        document.body.classList.add('wb-layout');
      }
    }
  },
  mounted() {
    if (!this.$route.meta.hideHeader) {
      document.body.classList.add('wb-layout');
    }
  },
  methods: {
    closeSidebarOnMobile(e) {
      if (window.innerWidth < 992 && this.sidebarOpen) {
        if (!e.target.closest('.sidebar')) this.sidebarOpen = false
      }
    }
  }
}
</script>

<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --sidebar-width: 260px;
  --topbar-height: 60px;
  --primary: #1e3a5f;
  --primary-light: #2d5282;
  --accent: #f59e0b;
  --accent-hover: #d97706;
  --bg: #f0f4f8;
  --card-bg: #ffffff;
  --text: #1a202c;
  --text-muted: #718096;
  --border: #e2e8f0;
  --success: #38a169;
  --danger: #e53e3e;
  --info: #3182ce;
  --radius: 10px;
  --shadow: 0 2px 12px rgba(0,0,0,0.08);
}

body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; color: var(--text); }
body.wb-layout { background: var(--bg); }

#app { display: flex; min-height: 100vh; }
#app > .login-page { flex: 1; }

.main-wrapper {
  flex: 1;
  margin-left: var(--sidebar-width);
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  transition: margin-left 0.3s ease;
}

.page-content {
  flex: 1;
  padding: 28px 32px;
  width: 100%;
  display: flex;
  flex-direction: column;
}

/* ── Page headers ── */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
}

.page-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--primary);
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-title .title-icon {
  width: 40px; height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--primary), var(--primary-light));
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 1.1rem;
}

/* ── Cards ── */
.wb-card {
  width: 100%;
  background: var(--card-bg);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  overflow: hidden;
}

.wb-card-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.wb-card-body { padding: 0; }

/* ── Search bar ── */
.search-bar {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 360px;
}

.search-bar input {
  width: 100%;
  padding: 8px 14px 8px 38px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 0.875rem;
  background: var(--bg);
  transition: border-color 0.2s, box-shadow 0.2s;
  outline: none;
  color: var(--text);
}

.search-bar input:focus { border-color: var(--primary-light); box-shadow: 0 0 0 3px rgba(45,82,130,0.12); }

.search-bar .search-icon {
  position: absolute;
  left: 11px; top: 50%; transform: translateY(-50%);
  color: var(--text-muted); font-size: 0.9rem; pointer-events: none;
}

/* ── Buttons ── */
.btn-wb-primary {
  background: var(--primary);
  color: #fff;
  border: none;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s, transform 0.1s;
  white-space: nowrap;
}

.btn-wb-primary:hover { background: var(--primary-light); transform: translateY(-1px); }

.btn-wb-danger { background: var(--danger); color: #fff; border: none; padding: 6px 10px; border-radius: 6px; cursor: pointer; font-size: 0.8rem; transition: opacity 0.2s; }
.btn-wb-danger:hover { opacity: 0.85; }

.btn-wb-info { background: var(--info); color: #fff; border: none; padding: 6px 10px; border-radius: 6px; cursor: pointer; font-size: 0.8rem; transition: opacity 0.2s; }
.btn-wb-info:hover { opacity: 0.85; }

.btn-wb-success { background: var(--success); color: #fff; border: none; padding: 6px 10px; border-radius: 6px; cursor: pointer; font-size: 0.8rem; transition: opacity 0.2s; }
.btn-wb-success:hover { opacity: 0.85; }

/* ── Tables ── */
.wb-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
.wb-table th { background: #f7fafc; color: var(--text-muted); font-weight: 600; text-transform: uppercase; font-size: 0.72rem; letter-spacing: 0.05em; padding: 12px 16px; text-align: left; border-bottom: 2px solid var(--border); }
.wb-table td { padding: 12px 16px; border-bottom: 1px solid var(--border); vertical-align: middle; color: var(--text); }
.wb-table tr:hover td { background: #f7fafc; }
.wb-table tr:last-child td { border-bottom: none; }

.wb-table td img { width: 44px; height: 44px; object-fit: cover; border-radius: 8px; cursor: pointer; border: 2px solid var(--border); transition: transform 0.2s; }
.wb-table td img:hover { transform: scale(1.1); }

/* ── Badges ── */
.badge-wb { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 20px; font-size: 0.72rem; font-weight: 600; }
.badge-wb-blue { background: #ebf4ff; color: var(--info); }
.badge-wb-green { background: #f0fff4; color: var(--success); }
.badge-wb-amber { background: #fffbeb; color: var(--accent-hover); }
.badge-wb-red { background: #fff5f5; color: var(--danger); }

/* ── Modals (override bootstrap-vue) ── */
.modal-content { border-radius: var(--radius) !important; border: none !important; box-shadow: 0 20px 60px rgba(0,0,0,0.15) !important; }
.modal-header { background: var(--primary) !important; color: #fff !important; border-radius: var(--radius) var(--radius) 0 0 !important; padding: 16px 24px !important; }
.modal-header .modal-title { font-weight: 700 !important; font-size: 1.1rem !important; }
.modal-header .close { color: #fff !important; opacity: 0.8; }
.modal-footer { border-top: 1px solid var(--border) !important; background: #f7fafc !important; padding: 12px 24px !important; }
.modal-body { padding: 24px !important; }

/* ── Form controls ── */
.form-control:focus { border-color: var(--primary-light) !important; box-shadow: 0 0 0 3px rgba(45,82,130,0.12) !important; }

/* ── Overlay ── */
.sidebar-overlay {
  display: none;
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 199;
}

/* ── Responsive ── */
@media (max-width: 991px) {
  .main-wrapper { margin-left: 0 !important; }
  .page-content { padding: 20px 16px; }
}

@media (max-width: 767px) {
  /* Tables: reduce padding on tablet */
  .wb-table th,
  .wb-table td { padding: 10px 10px; font-size: 0.82rem; }
  /* Modal body less padding */
  .modal-body { padding: 16px !important; }
  .modal-footer { padding: 10px 16px !important; }
}

@media (max-width: 575px) {
  .page-content { padding: 14px 10px; }
  .page-header { flex-direction: column; align-items: flex-start; gap: 10px; }
  .page-header button { width: 100%; justify-content: center; }
  .wb-card-header { flex-direction: column; align-items: flex-start; gap: 10px; }
  .search-bar { max-width: 100%; width: 100%; }
  /* Tables: tighten up on phones */
  .wb-table th,
  .wb-table td { padding: 8px 8px; font-size: 0.78rem; }
  .wb-table td img { width: 36px; height: 36px; }
  /* Badge smaller */
  .badge-wb { font-size: 0.68rem; padding: 2px 7px; }
  /* Buttons smaller */
  .btn-wb-primary,
  .btn-wb-danger,
  .btn-wb-info,
  .btn-wb-success { font-size: 0.8rem; padding: 7px 12px; }
  /* Modal fullscreen on mobile */
  .modal-dialog { margin: 0.5rem; }
  .modal-content { border-radius: 10px !important; }
  .modal-header { padding: 12px 16px !important; }
  .modal-body { padding: 14px !important; }
  .modal-footer { padding: 10px 14px !important; gap: 8px; flex-wrap: wrap; }
  .modal-footer button { flex: 1; min-width: 0; justify-content: center; }
}
</style>
